import { ChatWidgets } from "./ChatWidgets";
import { memo } from "react";
import { ChatActivityIndicator } from "@/components/ui/AiChatPanel/ChatActivityIndicator";
import { parseWidgetReferences } from "@/lib/data/schemas/widgets";
import { ChatArtifacts } from "./ChatArtifacts";
import { sandboxArtifactSchema } from "@/lib/data/schemas/sandbox-artifacts";
import type { AiChatTimelineItem, AiChatLayoutItem } from "@/lib/types/ai-chat";
import {
  CircleMinus,
  CircleAlert,
  Database,
  Files,
  Globe2,
  PencilLine,
  Wrench,
} from "lucide-react";
import { sandboxToolLabel } from "./sandboxToolPresentation";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/shadcn/Tooltip/Tooltip";

export type ChatToolGroupProps = {
  activity: AiChatLayoutItem["activity"];
  status: string | null;
};

type ToolCategory = "file" | "read" | "web" | "write" | "other";

export function isFlaggedToolActivity(activity: Record<string, unknown>) {
  return (
    activity.guardianRiskLevel === "high" &&
    (activity.guardianReviewStatus === "rejected" ||
      activity.guardianReviewStatus === "awaiting_human")
  );
}

function toolIdentity(
  item: Pick<AiChatTimelineItem, "payload" | "text">,
): string {
  const canonicalName = item.payload.toolName;
  if (typeof canonicalName === "string" && canonicalName.trim()) {
    return canonicalName.toLocaleLowerCase();
  }
  const legacyMetadata = ["qualifiedToolName", "namespace", "name"].flatMap(
    (key) => {
      const value = item.payload[key];
      return typeof value === "string" ? [value] : [];
    },
  );
  return [...legacyMetadata, item.text ?? ""].join(" ").toLocaleLowerCase();
}

export function classifyToolActivity(
  item: Pick<AiChatTimelineItem, "payload" | "text">,
): ToolCategory {
  const identity = toolIdentity(item);
  if (
    identity.includes("web_search") ||
    identity.includes("searching the web")
  ) {
    return "web";
  }
  if (
    sandboxToolLabel(identity) ||
    identity.includes("file") ||
    identity.includes("sandbox") ||
    identity.includes("document") ||
    identity.includes("documentation")
  ) {
    return "file";
  }
  if (
    identity.includes("updating lawworks") ||
    /(^|[._\s])(write|update|create|delete|remove|modify|edit|set|assign)([._\s]|$)/u.test(
      identity,
    )
  ) {
    return "write";
  }
  if (
    identity.includes("reading lawworks") ||
    identity.includes("checking lawworks data") ||
    identity.includes("finding a lawworks action") ||
    identity.includes("checking a lawworks action") ||
    /(^|[._\s])(sql|read|query|inspect|search|find|check|describe|list|get|view)([._\s]|$)/u.test(
      identity,
    )
  ) {
    return "read";
  }
  return "other";
}

function categoryLabel(category: ToolCategory) {
  if (category === "read") return "Searching LawWorks";
  if (category === "write") return "Updating case";
  if (category === "web") return "Searching the web";
  if (category === "file") return "Working with files";
  return "Tool activity";
}

function CategoryIcon({ category }: { category: ToolCategory }) {
  const className = "size-3.5 shrink-0";
  if (category === "read") return <Database className={className} />;
  if (category === "write") return <PencilLine className={className} />;
  if (category === "web") return <Globe2 className={className} />;
  if (category === "file") return <Files className={className} />;
  return <Wrench className={className} />;
}

export const ChatToolGroup = memo(function ChatToolGroup({
  activity,
  status,
}: ChatToolGroupProps) {
  const isActive = status === "queued" || status === "running";
  const count = (key: string, fallback: number) =>
    typeof activity[key] === "number" ? Math.max(0, activity[key]) : fallback;
  const toolCount = count("toolCount", 1);
  const cancelledCount = count(
    "cancelledCount",
    status === "cancelled" ? 1 : 0,
  );
  const flaggedCount = count(
    "flaggedCount",
    isFlaggedToolActivity(activity) ? 1 : 0,
  );
  const confirmationMessage =
    "One or more actions were flagged by automatic review. The AI may ask you to confirm your intent before continuing.";
  const categories = Array.isArray(activity.toolCategories)
    ? activity.toolCategories.filter(
        (category: unknown): category is ToolCategory =>
          typeof category === "string" &&
          ["file", "read", "web", "write", "other"].includes(category),
      )
    : [classifyToolActivity({ payload: activity, text: null })];
  const leadingCategory = categories.length === 1 ? categories[0] : "other";
  const labels = categories.map(categoryLabel);
  const label =
    labels.length < 2
      ? (labels[0] ?? "Tool activity")
      : `${labels.slice(0, -1).join(", ")}, and ${labels.at(-1)}`;
  const statusLabel = isActive
    ? "in progress"
    : cancelledCount
      ? `${cancelledCount} cancelled`
      : "complete";
  const artifacts = (
    Array.isArray(activity.artifacts) ? activity.artifacts : []
  )
    .slice(0, 20)
    .flatMap((artifact: unknown) => {
      const result = sandboxArtifactSchema.safeParse(artifact);
      return result.success ? [result.data] : [];
    });
  return (
    <div className="mx-auto w-full max-w-[60rem] px-5 py-2 sm:px-8">
      <div
        role="group"
        aria-label={`${label}, ${toolCount} ${toolCount === 1 ? "action" : "actions"}, ${statusLabel}`}
        className="text-muted-foreground flex min-h-7 max-w-full items-center gap-2 text-[13px] leading-6"
      >
        <ChatActivityIndicator active={isActive}>
          {cancelledCount ? (
            <CircleMinus className="size-3.5" />
          ) : (
            <CategoryIcon category={leadingCategory} />
          )}
        </ChatActivityIndicator>
        <span className="truncate">{label}</span>
        {flaggedCount > 0 && (
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  tabIndex={0}
                  role="img"
                  aria-label={confirmationMessage}
                  className="focus-visible:ring-ring shrink-0 rounded-full focus-visible:ring-2 focus-visible:outline-none"
                >
                  <CircleAlert className="size-3.5 text-amber-600 dark:text-amber-400" />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                {confirmationMessage}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {cancelledCount ? (
          <span className="shrink-0 opacity-70">
            {cancelledCount} cancelled
          </span>
        ) : null}
      </div>
      <ChatWidgets widgets={parseWidgetReferences(activity.widgets)} />
      {artifacts.length ? (
        <div className="mt-3">
          <ChatArtifacts artifacts={artifacts} />
        </div>
      ) : null}
    </div>
  );
});
